/**
 * Mobile Home — Indopoin desk + Nevi Watch (Paket 4).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Candidate, type MarketContext } from '../../lib/api';
import { fmt } from '../../lib/format';
import { brand, copy } from './brand';
import { TodayBoard } from './ui/TodayBoard';
import { MiniCandles, Sparkline } from './ui/Sparkline';
import { FabRun } from './ui/FabRun';
import { PullToRefresh } from './ui/PullToRefresh';
import { Skeleton } from './ui/Skeleton';
import { MobNum, MobMetric } from './ui/MobNum';
import { useKlines } from './hooks/useKlines';

export function MobileDashboardPage() {
  const qc = useQueryClient();

  const { data: candidatesData, isLoading: loadingCand } = useQuery({
    queryKey: ['candidates', 'latest-cycle'],
    queryFn: () =>
      api.get<{
        candidates: Candidate[];
        count: number;
        cycle_id?: string | null;
        stats?: { total: number; passed: number; rejected: number };
      }>('/api/bot/candidates?limit=400&cycle_id=latest'),
    refetchInterval: 30_000,
  });

  const { data: ctxBtc } = useQuery({
    queryKey: ['context', 'BTCUSDT'],
    queryFn: () => api.get<MarketContext>('/api/data/context?symbol=BTCUSDT'),
    refetchInterval: 60_000,
  });

  const { data: activityWrap } = useQuery({
    queryKey: ['bot-activity'],
    queryFn: () =>
      api.get<{
        activity: {
          running: boolean;
          cycle_id: string | null;
          steps: Array<{ at: string; level: string; message: string }>;
          result: any;
        };
      }>('/api/bot/activity'),
    refetchInterval: (q) => ((q.state.data as any)?.activity?.running ? 1000 : 8000),
  });
  const activity = activityWrap?.activity;

  const klines = useKlines('BTCUSDT', '1h', 42);

  const runBot = useMutation({
    mutationFn: () => api.post<any>('/api/bot/run', { narrate: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
      qc.invalidateQueries({ queryKey: ['bot-activity'] });
      qc.invalidateQueries({ queryKey: ['context'] });
      qc.invalidateQueries({ queryKey: ['klines'] });
      if (navigator.vibrate) navigator.vibrate([10, 40, 10]);
    },
    onError: () => qc.invalidateQueries({ queryKey: ['bot-activity'] }),
  });

  const stats = candidatesData?.stats;
  const candidates = candidatesData?.candidates || [];
  const isPass = (c: Candidate) => c.reject_reason == null || String(c.reject_reason).trim() === '';
  const passed = candidates.filter(isPass).length;
  const rejected = candidates.length - passed;
  const total = stats?.total ?? candidates.length;
  const displayPassed = stats?.passed ?? passed;
  const displayRejected = stats?.rejected ?? rejected;
  const running = runBot.isPending || !!activity?.running;

  const top = [...candidates.filter(isPass)]
    .sort((a, b) => Number(a.soft_score) - Number(b.soft_score))
    .slice(0, 5);

  const btcLabel = fmt.price(ctxBtc?.spot_price);
  const chartMode = (klines.data?.candles?.length ?? 0) >= 8 ? 'candles' : 'spark';

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['candidates'] }),
      qc.invalidateQueries({ queryKey: ['context'] }),
      qc.invalidateQueries({ queryKey: ['bot-activity'] }),
      qc.invalidateQueries({ queryKey: ['klines'] }),
    ]);
  };

  return (
    <div className="tc-mob-page tc-mob-page--ht tc-mob-page--eg">
      <PullToRefresh onRefresh={refresh}>
        <div className="tc-mob-page__header tc-mob-page__header--eg">
          <div className="tc-mob-page__title">{copy.home.title}</div>
          <div className="tc-mob-page__sub">{copy.home.sub}</div>
        </div>

        <TodayBoard
          btc={btcLabel}
          fng={ctxBtc?.fear_greed != null ? ctxBtc.fear_greed : '—'}
          passed={displayPassed}
          total={total}
          running={running}
          chart={
            klines.isLoading ? (
              <Skeleton variant="chart" />
            ) : chartMode === 'candles' && klines.data ? (
              <MiniCandles candles={klines.data.candles.slice(-36)} height={96} />
            ) : klines.data ? (
              <Sparkline values={klines.data.closes} height={80} />
            ) : null
          }
        />

        <div className="tc-mob-ht-panel tc-mob-ht-panel--eg">
          <div className="tc-mob-ht-panel__row">
            <div>
              <div className="tc-mob-ht-panel__label">DVOL</div>
              <MobNum tone="cyan" size="md">
                {fmt.percent(ctxBtc?.dvol, 1)}
              </MobNum>
            </div>
            <div>
              <div className="tc-mob-ht-panel__label">Funding</div>
              <MobNum size="md">
                {fmt.percent(
                  ctxBtc?.funding_rate != null ? Number(ctxBtc.funding_rate) * 100 : null,
                  3,
                )}
              </MobNum>
            </div>
            <div>
              <div className="tc-mob-ht-panel__label">BTC 1h</div>
              <MobNum
                tone={
                  klines.data && klines.data.closes.length > 1
                    ? klines.data.closes[klines.data.closes.length - 1] >= klines.data.closes[0]
                      ? 'green'
                      : 'red'
                    : 'muted'
                }
                size="md"
              >
                {klines.data && klines.data.closes.length > 1
                  ? (() => {
                      const a = klines.data.closes[0];
                      const b = klines.data.closes[klines.data.closes.length - 1];
                      const pct = ((b - a) / a) * 100;
                      return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
                    })()
                  : '—'}
              </MobNum>
            </div>
          </div>
        </div>

        <button
          type="button"
          className="tc-mob-run-btn tc-mob-run-btn--ht tc-mob-run-btn--eg"
          onClick={() => runBot.mutate()}
          disabled={running}
        >
          {running ? copy.home.ctaRunning : copy.home.cta}
        </button>
        <p className="tc-mob-tagline">{brand.tagline}</p>

        <div className="tc-mob-stats-bar tc-mob-stats-bar--ht tc-mob-stats-bar--eg">
          <div className="tc-mob-stat">
            <div className="tc-mob-stat__num tc-mob-stat__num--green">{displayPassed}</div>
            <div className="tc-mob-stat__label">{copy.home.statsPass}</div>
          </div>
          <div className="tc-mob-stat__sep" />
          <div className="tc-mob-stat">
            <div className="tc-mob-stat__num tc-mob-stat__num--red">{displayRejected}</div>
            <div className="tc-mob-stat__label">{copy.home.statsReject}</div>
          </div>
          <div className="tc-mob-stat__sep" />
          <div className="tc-mob-stat">
            <div className="tc-mob-stat__num">{total}</div>
            <div className="tc-mob-stat__label">{copy.home.statsTotal}</div>
          </div>
        </div>

        <div className="tc-mob-section">
          <div className="tc-mob-section__title">
            <span className="tc-mob-section__pipe" /> {copy.home.recTitle}
          </div>
          {loadingCand ? (
            <Skeleton rows={3} />
          ) : top.length === 0 ? (
            <div className="tc-mob-empty">{copy.home.recEmpty}</div>
          ) : (
            <div className="tc-mob-card-list">
              {top.map((c, idx) => (
                <div key={c.id} className="tc-mob-card tc-mob-card--ht tc-mob-card--eg">
                  <div className="tc-mob-card__rank">#{idx + 1}</div>
                  <div className="tc-mob-card__header">
                    <span
                      className={`tc-mob-badge tc-mob-badge--${c.option_type === 'PUT' ? 'cyan' : 'amber'}`}
                    >
                      {c.option_type}
                    </span>
                    <span className="tc-mob-card__symbol">{c.underlying}</span>
                    <MobNum className="tc-mob-card__score" size="sm" full={fmt.score(c.soft_score)}>
                      {fmt.score(c.soft_score)}
                    </MobNum>
                  </div>
                  <div className="tc-mob-card__body tc-mob-card__body--metrics3">
                    <MobMetric
                      label="Strike"
                      value={fmt.compact(c.strike_price, 2)}
                      full={fmt.number(c.strike_price, 0)}
                    />
                    <MobMetric
                      label="APR"
                      value={fmt.percent(c.apr, 2)}
                      full={fmt.percent(c.apr, 4)}
                      tone="green"
                    />
                    <MobMetric label="Settle" value={fmt.countdown(c.settle_date)} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {activity && (
          <div className="tc-mob-section">
            <div className="tc-mob-section__title">
              <span className="tc-mob-section__pipe" /> {copy.home.logTitle}{' '}
              {activity.running && <span className="tc-mob-live-dot" />}
            </div>
            <div className="tc-mob-log tc-mob-log--ht tc-mob-log--eg">
              {(activity.steps || []).slice(-8).map((s, i) => (
                <div key={i} className={`tc-mob-log__line tc-mob-log__line--${s.level}`}>
                  <span className="tc-mob-log__time">
                    {s.at
                      ? new Date(s.at).toLocaleTimeString('id-ID', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : ''}
                  </span>
                  <span className="tc-mob-log__msg">{s.message}</span>
                </div>
              ))}
              {(activity.steps || []).length === 0 && (
                <div className="tc-mob-empty" style={{ padding: '12px 0' }}>
                  {copy.home.logEmpty}
                </div>
              )}
            </div>
          </div>
        )}
      </PullToRefresh>

      <FabRun running={running} onClick={() => runBot.mutate()} />
    </div>
  );
}
